import React from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Switch, // <-- già presente
} from "@material-ui/core";
import { useIntl } from "react-intl";
import { notifications } from "../messages";
import NumberInput from "../ui/NumberInput";

// ---- Helpers per gestione immagini nel corpo email ----
const IMG_EXTS = [
  "png",
  "jpg",
  "jpeg",
  "gif",
  "webp",
  "svg",
  "bmp",
  "ico",
  "tiff",
  "avif",
];

const IMG_REGEX = new RegExp(
  String.raw`images\/[^"'()<>\s]+\.(?:${IMG_EXTS.join("|")})`,
  "gi"
);

const joinPath = (base, name) => {
  const b0 = typeof base === "string" ? base.trim() : "";
  const n0 = typeof name === "string" ? name : String(name ?? "");
  if (!b0) return n0;
  const b = b0.endsWith("/") ? b0.slice(0, -1) : b0;
  const n = n0.startsWith("/") ? n0.slice(1) : n0;
  return `${b}/${n}`;
};

/**
 * Props:
 * - value: object (notifica/draft) con chiavi:
 *   active,
 *   in_app_active, in_app_title, in_app_body,
 *   push_active, push_title, push_body, push_image_url,
 *   banner_active, banner_title, banner_body,
 *   email_active, email_from, email_from_name, email_subject, email_body,
 *   consent_id, remind_days, event_type
 * - onChange: (key: string, val: any) => void
 * - consents: array<{ id, title }>
 * - imagesPath?: string | null
 * - notifConfig?: { events?: Array<{type: string, type_name: string, offset_type: string}> }
 */
export default function NotificationCommonFields({
  value,
  onChange,
  consents,
  imagesPath,
  eventConfig,
}) {
  const intl = useIntl();
  const [previewOpen, setPreviewOpen] = React.useState(false);

  // required condizionale
  const reqInApp = !!value?.in_app_active;
  const reqPush = !!value?.push_active;
  const reqBanner = !!value?.banner_active;
  const reqEmail = !!value?.email_active;

  // ---- SWITCH "Abilitato/Disabilitato" ----
  const [checked, setChecked] = React.useState(!!value?.active);
  React.useEffect(() => setChecked(!!value?.active), [value?.active]);
  const enabledLabel = checked ? "Abilitato" : "Disabilitato";
  const handleToggleActive = (e) => {
    const next = !!e.target.checked;
    setChecked(next);
    onChange("active", next);
  };

  // consensi ordinati alfab.
  const sortedConsents = React.useMemo(() => {
    if (!Array.isArray(consents)) return [];
    const copy = consents.slice();
    copy.sort((a, b) => {
      const at = (a?.title ?? "").toString().trim();
      const bt = (b?.title ?? "").toString().trim();
      const cmp = at.localeCompare(bt, undefined, {
        sensitivity: "base",
        numeric: true,
      });
      return cmp !== 0
        ? cmp
        : String(a?.id ?? "").localeCompare(String(b?.id ?? ""), undefined, {
            numeric: true,
          });
    });
    return copy;
  }, [consents]);

  // ⬇️ AUTO-SELEZIONE se c'è un solo consenso
  React.useEffect(() => {
    if (sortedConsents.length === 1) {
      const only = sortedConsents[0];
      const current = value?.consent_id ?? "";
      if (current !== only.id) {
        onChange("consent_id", only.id);
      }
    }
  }, [sortedConsents, value?.consent_id, onChange]);

  // elenco deduplicato delle occorrenze images/<file> in email_body
  const imagesMatches = React.useMemo(() => {
    const base = typeof imagesPath === "string" ? imagesPath.trim() : "";
    if (!base) return [];
    const htmlRaw = value?.email_body;
    const html = typeof htmlRaw === "string" ? htmlRaw : String(htmlRaw ?? "");
    if (!html) return [];
    const all = html.match(IMG_REGEX) || [];
    const seen = new Set();
    const list = [];
    for (const m of all) {
      const key = m.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        list.push(m);
      }
    }
    return list;
  }, [value?.email_body, imagesPath]);

  const doReplaceImages = () => {
    const base = typeof imagesPath === "string" ? imagesPath.trim() : "";
    if (!base) return;
    const htmlRaw = value?.email_body;
    const html = typeof htmlRaw === "string" ? htmlRaw : String(htmlRaw ?? "");
    if (!html) return;
    const replacedHtml = html.replace(IMG_REGEX, (match) => {
      const filename = match.replace(/^images\//i, "");
      return joinPath(base, filename);
    });
    onChange("email_body", replacedHtml);
  };

  // --- Leggi offset_type da notifConfig.events per l'event_type corrente ---
  const offsetType = React.useMemo(() => {
    return (eventConfig?.offset_type || "").toString();
  }, [eventConfig]);

  const getDaysLabel = (ot) => {
    if (!ot) return "";
    switch (ot) {
      case "DAYS_BEFORE_EXPIRY":
        return intl.formatMessage(notifications.daysBeforeExpiry);
      case "DAYS_BEFORE_SLOT":
        return intl.formatMessage(notifications.daysBeforeSlot);
      case "DAYS_AFTER_EVENT":
        return intl.formatMessage(notifications.daysAfterEvent);
      default:
        return ot;
    }
  };

  // Label per remind_days in base a offset_type
  const daysLabel = getDaysLabel(offsetType);

  return (
    <Box style={{ width: "100%" }}>
      {/* SWITCH Abilitato/Disabilitato */}
      <Box mb={offsetType ? 1 : 2}>
        <FormControlLabel
          control={
            <Switch
              checked={checked}
              onChange={handleToggleActive}
              color="primary"
              inputProps={{ "aria-label": "toggle active" }}
            />
          }
          label={
            <Typography variant="body2" style={{ fontWeight: 500 }}>
              {enabledLabel}
            </Typography>
          }
        />
      </Box>

      {/* REMIND DAYS (opzionale) */}
      {offsetType && (
        <NumberInput
          label={daysLabel}
          value={value?.remind_days}
          onChange={(val) => onChange("remind_days", val)}
          fullWidth
          max={9999}
        />
      )}

      {/* CONSENSI */}
      <Box mb={2} mt={offsetType ? 2 : 0}>
        <FormControl fullWidth>
          <InputLabel id="consent-select-label">Consenso</InputLabel>
          <Select
            labelId="consent-select-label"
            id="consent-select"
            value={value?.consent_id ?? ""} // "" = non necessario
            label="Consenso"
            onChange={(e) => onChange("consent_id", e.target.value)}
          >
            {sortedConsents.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* IN-APP */}
      <Box mt={3}>
        <FormControlLabel
          control={
            <Checkbox
              checked={!!value?.in_app_active}
              onChange={(e) => onChange("in_app_active", e.target.checked)}
              color="primary"
            />
          }
          label="Notifica in app"
        />
        {value?.in_app_active && (
          <TextField
            margin="dense"
            label={"Titolo"}
            value={value?.in_app_title ?? ""}
            onChange={(e) => onChange("in_app_title", e.target.value)}
            fullWidth
            required={reqInApp}
          />
        )}
        {value?.in_app_active && (
          <TextField
            margin="dense"
            label={"Testo"}
            value={value?.in_app_body ?? ""}
            onChange={(e) => onChange("in_app_body", e.target.value)}
            fullWidth
            multiline
            required={reqInApp}
          />
        )}
      </Box>

      {/* PUSH */}
      <Box mt={3}>
        <FormControlLabel
          control={
            <Checkbox
              checked={!!value?.push_active}
              onChange={(e) => onChange("push_active", e.target.checked)}
              color="primary"
            />
          }
          label="Notifica push"
        />
        {value?.push_active && (
          <TextField
            margin="dense"
            label={"Titolo"}
            value={value?.push_title ?? ""}
            onChange={(e) => onChange("push_title", e.target.value)}
            fullWidth
            required={reqPush}
          />
        )}
        {value?.push_active && (
          <TextField
            margin="dense"
            label={"Testo"}
            value={value?.push_body ?? ""}
            onChange={(e) => onChange("push_body", e.target.value)}
            fullWidth
            multiline
            required={reqPush}
          />
        )}
        {value?.push_active && (
          <TextField
            margin="dense"
            label={"Url Img"}
            value={value?.push_image_url ?? ""}
            onChange={(e) => onChange("push_image_url", e.target.value)}
            fullWidth
            multiline
          />
        )}
      </Box>

      {/* BANNER */}
      <Box mt={3}>
        <FormControlLabel
          control={
            <Checkbox
              checked={!!value?.banner_active}
              onChange={(e) => onChange("banner_active", e.target.checked)}
              color="primary"
            />
          }
          label="Notifica Banner"
        />
        {value?.banner_active && (
          <TextField
            margin="dense"
            label={"Titolo"}
            value={value?.banner_title ?? ""}
            onChange={(e) => onChange("banner_title", e.target.value)}
            fullWidth
            required={reqBanner}
          />
        )}
        {value?.banner_active && (
          <TextField
            margin="dense"
            label={"Testo"}
            value={value?.banner_body ?? ""}
            onChange={(e) => onChange("banner_body", e.target.value)}
            fullWidth
            multiline
            required={reqBanner}
          />
        )}
      </Box>

      {/* EMAIL */}
      <Box mt={3}>
        <FormControlLabel
          control={
            <Checkbox
              checked={!!value?.email_active}
              onChange={(e) => onChange("email_active", e.target.checked)}
              color="primary"
            />
          }
          label="Notifica email"
        />
        {value?.email_active && (
          <>
            <TextField
              margin="dense"
              label={"Email mittente"}
              value={value?.email_from ?? ""}
              onChange={(e) => onChange("email_from", e.target.value)}
              fullWidth
              required={reqEmail}
            />
            <TextField
              margin="dense"
              label={"Nome mittente"}
              value={value?.email_from_name ?? ""}
              onChange={(e) => onChange("email_from_name", e.target.value)}
              fullWidth
              required={reqEmail}
            />
            <TextField
              margin="dense"
              label={"Oggetto"}
              value={value?.email_subject ?? ""}
              onChange={(e) => onChange("email_subject", e.target.value)}
              fullWidth
              required={reqEmail}
            />

            {typeof imagesPath === "string" &&
              imagesPath.trim() !== "" &&
              imagesMatches.length > 0 && (
                <Box
                  mt={1.5}
                  mb={1}
                  p={1}
                  borderRadius={6}
                  style={{ background: "#f5f9ff", border: "1px solid #e0ecff" }}
                >
                  <Typography variant="body2" gutterBottom>
                    Trovate {imagesMatches.length}{" "}
                    {imagesMatches.length === 1 ? "immagine" : "immagini"} nel
                    corpo dell’email con percorso relativo:
                  </Typography>
                  <Box
                    component="ul"
                    m={0}
                    pl={3}
                    style={{
                      maxHeight: 140,
                      overflow: "auto",
                      fontFamily:
                        'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                      fontSize: "0.85em",
                    }}
                  >
                    {imagesMatches.map((m, idx) => (
                      <li key={`${m}-${idx}`}>
                        <code>{m}</code>
                      </li>
                    ))}
                  </Box>
                  <Box mt={1}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={doReplaceImages}
                    >
                      Sostituisci
                    </Button>
                  </Box>
                </Box>
              )}

            <Box display="flex" justifyContent="flex-end" mt={1} mb={0.5}>
              <Button
                onClick={() => setPreviewOpen(true)}
                size="small"
                variant="text"
                style={{ fontSize: "0.75rem", padding: "2px 6px" }}
              >
                Anteprima
              </Button>
            </Box>

            <TextField
              margin="dense"
              label={"Testo (HTML)"}
              value={value?.email_body ?? ""}
              onChange={(e) => onChange("email_body", e.target.value)}
              fullWidth
              required={reqEmail}
              multiline
              rows={14}
              inputProps={{
                wrap: "off",
                spellCheck: "false",
                autoCorrect: "off",
                autoCapitalize: "off",
                style: {
                  fontFamily:
                    'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                  whiteSpace: "pre",
                  overflow: "auto",
                  overflowWrap: "normal",
                  lineHeight: 1.5,
                  tabSize: 2,
                },
              }}
            />

            <Dialog
              open={previewOpen}
              onClose={() => setPreviewOpen(false)}
              fullWidth
              maxWidth="md"
            >
              <DialogTitle>Anteprima email</DialogTitle>
              <DialogContent dividers>
                <div
                  style={{ width: "100%" }}
                  dangerouslySetInnerHTML={{ __html: value?.email_body || "" }}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setPreviewOpen(false)} color="primary">
                  Chiudi
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
      </Box>
    </Box>
  );
}
